/**
 * ClientsManager Component
 *
 * Manages clients: list, add, edit, delete.
 */
import { useState, useCallback } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { DataTable, ConfirmDialog, type Column } from '../ui';
import { ClientForm } from './ClientForm';
import type { Client, ClientFormData } from '../../types';

export const ClientsManager = () => {
  const {
    clients,
    isLoading,
    createClient,
    updateClient,
    deleteClient,
    getReportCountByClient,
    getUserCountByClient,
  } = useAdmin();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Open form for adding
  const handleAdd = () => {
    setSelectedClient(null);
    setIsFormOpen(true);
  };

  // Open form for editing
  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setIsFormOpen(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (client: Client) => {
    setSelectedClient(client);
    setIsDeleteDialogOpen(true);
  };

  // Submit form (create or update)
  const handleFormSubmit = useCallback(
    async (data: ClientFormData) => {
      setIsSubmitting(true);
      try {
        if (selectedClient) {
          await updateClient(selectedClient.id, data);
        } else {
          await createClient(data);
        }
        setIsFormOpen(false);
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedClient, createClient, updateClient]
  );

  // Confirm delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedClient) return;

    setIsSubmitting(true);
    try {
      await deleteClient(selectedClient.id);
      setIsDeleteDialogOpen(false);
      setSelectedClient(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedClient, deleteClient]);

  // Table columns
  const columns: Column<Client>[] = [
    {
      key: 'logo',
      header: 'Logo',
      width: '80px',
      render: (client) =>
        client.logo ? (
          <img
            src={client.logo}
            alt={client.name}
            className="w-10 h-10 rounded object-contain bg-gray-100"
          />
        ) : (
          <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        ),
    },
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'color',
      header: 'Color',
      width: '100px',
      render: (client) => (
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border border-gray-200"
            style={{ backgroundColor: client.color }}
          />
          <span className="text-xs font-mono text-gray-500">{client.color}</span>
        </div>
      ),
    },
    {
      key: 'reports',
      header: 'Reports',
      width: '80px',
      render: (client) => (
        <span className="text-gray-600">{getReportCountByClient(client.id)}</span>
      ),
    },
    {
      key: 'users',
      header: 'Users',
      width: '80px',
      render: (client) => (
        <span className="text-gray-600">{getUserCountByClient(client.id)}</span>
      ),
    },
  ];

  // Check if client can be deleted (has no users or reports)
  const canDeleteClient = (client: Client) => {
    return getReportCountByClient(client.id) === 0 && getUserCountByClient(client.id) === 0;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Clients</h3>
        <button
          onClick={handleAdd}
          className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green"
        >
          + Add Client
        </button>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={clients}
        loading={isLoading}
        keyExtractor={(c) => c.id}
        emptyMessage="No clients yet. Add your first client to get started."
        actions={(client) => (
          <>
            <button
              onClick={() => handleEdit(client)}
              className="p-1 text-gray-400 hover:text-metrica-green transition-colors"
              title="Edit"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => handleDeleteClick(client)}
              disabled={!canDeleteClient(client)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={canDeleteClient(client) ? 'Delete' : 'Cannot delete: has users or reports'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
      />

      {/* Add/Edit Form */}
      <ClientForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        client={selectedClient}
        isLoading={isSubmitting}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Client"
        message={`Are you sure you want to delete "${selectedClient?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default ClientsManager;
